import { MARKET_TZ, matchSessionId } from '../../services/indicators/sessionWindows';

/** Trade-time filter. Independent of the broker bar session (RTH vs extended hours). Times are ET. */
export const TRADE_SESSION_PRESETS = [
  { id: 'any', label: 'Any time' },
  { id: 'am', label: 'AM Session' },
  { id: 'pm', label: 'PM Session' },
  { id: 'rth', label: 'RTH' },
  { id: 'premarket', label: 'Premarket' },
  { id: 'afterhours', label: 'After Hours' },
  { id: 'custom', label: 'Custom' },
];

const PRESET_WINDOWS = {
  am: { start: '09:30', end: '12:00' },
  pm: { start: '12:00', end: '16:00' },
  rth: { start: '09:30', end: '16:00' },
  premarket: { start: '04:00', end: '09:30' },
  afterhours: { start: '16:00', end: '20:00' },
};

export const DEFAULT_TRADE_SESSION = {
  preset: 'any',
  start: '09:30',
  end: '16:00',
};

const CLOCK_RE = /^(\d{1,2}):(\d{2})$/;

export function normalizeTradeSession(raw) {
  const preset = TRADE_SESSION_PRESETS.some((p) => p.id === raw?.preset)
    ? raw.preset
    : DEFAULT_TRADE_SESSION.preset;
  return {
    preset,
    start: normalizeClock(raw?.start, DEFAULT_TRADE_SESSION.start),
    end: normalizeClock(raw?.end, DEFAULT_TRADE_SESSION.end),
  };
}

function normalizeClock(value, fallback) {
  const match = String(value ?? '').trim().match(CLOCK_RE);
  if (!match) return fallback;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return fallback;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function tradeSessionActive(filter) {
  return Boolean(filter && filter.preset && filter.preset !== 'any');
}

function windowFor(filter) {
  if (!tradeSessionActive(filter)) return null;
  if (filter.preset === 'custom') {
    return { id: 'custom', start: filter.start, end: filter.end, enabled: true };
  }
  const preset = PRESET_WINDOWS[filter.preset];
  if (!preset) return null;
  return { id: filter.preset, ...preset, enabled: true };
}

export function etMinutesOfDay(bar) {
  const ms = bar?.timeSec != null
    ? Number(bar.timeSec) * 1000
    : Date.parse(bar?.timestamp || bar?.TimeStamp || '');
  if (!Number.isFinite(ms)) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MARKET_TZ,
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const hour = Number(parts.find((p) => p.type === 'hour')?.value);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return (hour % 24) * 60 + minute;
}

/** True when no session is attached, or the bar falls inside the attached ET window. */
export function barInTradeSession(bar, filter) {
  const window = windowFor(filter);
  if (!window) return true;
  const minutes = etMinutesOfDay(bar);
  if (minutes == null) return false;
  return matchSessionId(minutes, [window]) != null;
}

/** Boxes the chart can paint for the attached window. Empty when the filter is off. */
export function tradeSessionBoxes(bars, filter) {
  const window = windowFor(filter);
  if (!window) return [];
  const boxes = [];
  let current = null;
  const list = bars || [];
  for (let i = 0; i < list.length; i++) {
    const bar = list[i];
    if (!barInTradeSession(bar, filter)) {
      if (current) {
        boxes.push(current);
        current = null;
      }
      continue;
    }
    const high = Number(bar.high ?? bar.High);
    const low = Number(bar.low ?? bar.Low);
    if (!current) {
      current = {
        sessionKey: window.id,
        startIndex: i,
        endIndex: i,
        high,
        low,
      };
      continue;
    }
    current.endIndex = i;
    if (Number.isFinite(high)) current.high = Math.max(current.high, high);
    if (Number.isFinite(low)) current.low = Math.min(current.low, low);
  }
  if (current) boxes.push(current);
  return boxes;
}
